import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PasswordRoundedIcon from '@mui/icons-material/PasswordRounded';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import type { FC, JSX } from 'react';

import PendingIcon from 'components/Icons/PendingIcon';
import CustomActivityLabel from 'components/MuiCustom/CustomActivityLabel';

import type { DeviceActivityStatus } from 'redux-store/slices/ui/types';

interface ActivityLabelProps {
  status: DeviceActivityStatus;
  onClick?: React.MouseEventHandler<HTMLElement>;
}

const ActivityLabel: FC<ActivityLabelProps> = ({ status, onClick }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const getStatusProperties = (): {
    backgroundColor: string;
    textColor: string;
    statusText: string;
    icon: JSX.Element;
    opacity: number;
  } | null => {
    if (!status) return null;

    switch (status) {
      case 'Failed':
        return {
          backgroundColor: '#ff3b3024',
          textColor: 'error',
          statusText: t('common:failed'),
          icon: <ErrorOutlineIcon color="error" fontSize="small" />,
          opacity: 0.7,
        };
      case 'Pending':
        return {
          backgroundColor: '#ff950024',
          textColor: theme.vars.palette.warning.main,
          statusText: t('common:pending'),
          icon: <PendingIcon sx={{ color: theme.vars.palette.warning.main }} />,
          opacity: 0.7,
        };
      case 'Protected':
        return {
          backgroundColor: '#5856D626',
          textColor: '#3634A2',
          statusText: t('common:restricted'),
          icon: (
            <PasswordRoundedIcon sx={{ color: '#3634A2' }} fontSize="small" />
          ),
          opacity: 0.7,
        };
      case 'Completed':
        return {
          backgroundColor: '#34c7592e',
          textColor: theme.vars.palette.success.main,
          statusText: t('common:completed'),
          icon: <CheckIcon color="success" fontSize="small" />,
          opacity: 1,
        };
    }
  };

  const statusProperties = getStatusProperties();

  if (!statusProperties) {
    return null;
  }

  const { backgroundColor, textColor, statusText, icon, opacity } =
    statusProperties;

  return (
    <Box display="flex" flexDirection="row" gap={0.5}>
      <CustomActivityLabel
        opacity={opacity}
        backgroundColor={backgroundColor}
        onClick={onClick}
        isSmallScreen={smallScreen}
        icon={icon}
        textColor={textColor}
        statusText={statusText}
      />
    </Box>
  );
};

export default ActivityLabel;
